import ClassNames from "classnames";
import { FunctionComponent, useEffect, useState } from "react";
import tinycolors from "tinycolor2";
import { selectActiveSite } from "../../selectors/sites.js";
import { useAppSelector } from "../../store/hooks.js";
import styles from "../../stylesheets/components/quick-enquiry-module.module.css";
import {
  BaseModuleProps,
  QuickEnquiryModuleSettings,
  QuickEnquiryWidgetSettings,
} from "../../types/index.js";
import {
  getActiveColorScheme,
  getSupportedLanguage,
} from "../../utils/utils.js";
import QuickEnquiryWidget from "../QuickEnquiryWidget.js";

interface Props extends BaseModuleProps<QuickEnquiryModuleSettings> {}

const QuickEnquiryModule: FunctionComponent<Props> = ({
  translatedModule: {
    translation: { languageId },
    settings: { layout, fieldSettings, badgeStyle },
  },
  translatedModule,
  isActive,
  isPreview,
}) => {
  const site = useAppSelector(selectActiveSite);
  const scheme = useAppSelector((state) =>
    getActiveColorScheme(state.colorSchemes, site, translatedModule),
  );

  const settings: QuickEnquiryWidgetSettings = {
    fieldSettings,
    propertyId: site.accommodation.id,
    lang: getSupportedLanguage(languageId, ["de", "it", "en", "fr"]),
    layout,
    badgeStyle,
    // Show form by default in preview (CMS)
    onLoad: ({ showForm }) => showForm(isPreview),
    colorScheme: {
      ...scheme,
      main: {
        ...scheme.main,
        separator: tinycolors(scheme.main.separator).lighten(2).toRgbString(),
      },
    },
  };

  const [isShown, setIsShown] = useState(isPreview);

  useEffect(() => {
    const showWidget = () => {
      setIsShown(true);
      document.removeEventListener("scroll", showWidget);
    };

    // If the document is already scrolled vertically,
    // load the widget immediately, otherwise
    // wait until the user scrolls.
    window.scrollY
      ? showWidget()
      : document.addEventListener("scroll", showWidget);

    return () => {
      document.removeEventListener("scroll", showWidget);
    };
  }, []);

  // Don’t render anything before accommodation is loaded
  // Hide the module if it’s in preview and inactive
  return !settings.propertyId || (isPreview && !isActive) || !isShown ? null : (
    <div
      className={ClassNames(styles.QuickEnquiryModule, {
        [styles["QuickEnquiryModule--preview"]]: isPreview,
      })}
    >
      <QuickEnquiryWidget settings={settings} />
    </div>
  );
};

export default QuickEnquiryModule;
